import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Loader2, Upload } from "lucide-react";

import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import {
  CFE_IMPORT_TRAIL_STEP,
  DGI_CONFIG_TRAIL,
} from "@/components/onboarding/OnboardingTrail";
import { Button } from "@/components/ui/button";
import { ImportEmittedCfeDialog } from "@/pages/tenants/ImportEmittedCfeDialog";
import { ImportRecibidasCfeDialog } from "@/pages/tenants/ImportRecibidasCfeDialog";
import {
  importEmittedCfes,
  importRecibidasCfes,
} from "@/services/invoiceService";

export interface OnboardingCfeImportStepProps {
  tenantId: string;
  tenantDisplayName: string;
  /** Called after a successful import (emitted or received) so onboarding can enable Next. */
  onImportSuccess?: () => void;
  /** Renders the inline "Podés omitir este paso..." banner + Omitir button. */
  onSkip?: () => void;
  /** Back nav (renders left "Volver" button when provided). */
  onBack?: () => void;
  /** Next nav (renders right "Siguiente" button when provided). */
  onNext?: () => void | Promise<void>;
  /** Disable the Next button (e.g., until an import has succeeded). */
  nextDisabled?: boolean;
  /** Show a spinner on the Next button while it works. */
  nextLoading?: boolean;
}

function CfeImportBrandColumn(): JSX.Element {
  const { t } = useTranslation();
  return (
    <OnboardingBrandColumn
      eyebrow={t(
        "onboarding.cfeImport.brandEyebrow",
        "Tus comprobantes anteriores",
      )}
      quote={
        <>
          {t(
            "onboarding.cfeImport.brandQuoteLead",
            "Importá lo que ya emitiste —",
          )}{" "}
          <span className="accent">
            {t(
              "onboarding.cfeImport.brandQuoteAccent",
              "tu historial fiscal completo en un solo lugar.",
            )}
          </span>
        </>
      }
      currentStep={4}
      trailSteps={[...DGI_CONFIG_TRAIL, CFE_IMPORT_TRAIL_STEP]}
    />
  );
}

export function OnboardingCfeImportStep({
  tenantId,
  tenantDisplayName,
  onImportSuccess,
  onSkip,
  onBack,
  onNext,
  nextDisabled = false,
  nextLoading = false,
}: OnboardingCfeImportStepProps): JSX.Element {
  const { t } = useTranslation();
  const [importEmittedOpen, setImportEmittedOpen] = useState(false);
  const [importRecibidasOpen, setImportRecibidasOpen] = useState(false);

  return (
    <>
      <OnboardingShell
        form={
          <>
            <OnboardingBrandMark />
            <OnboardingStepTag
              step={4}
              total={4}
              label={t("onboarding.cfeImport.tag", "CFE anteriores")}
              flow={t("onboarding.cfeImportStep.flow", "Configuración DGI")}
            />
            <h1 className="fx-onb-h1">
              {t(
                "onboarding.cfeImport.h1Lead",
                "Importá tus comprobantes",
              )}{" "}
              <span className="ital">
                {t("onboarding.cfeImport.h1Accent", "anteriores.")}
              </span>
            </h1>
            <p className="fx-onb-lead">
              {t(
                "onboarding.cfeImport.lead",
                "Si tenés exportaciones en ZIP o XML de tu sistema anterior o de DGI, subilas acá. Podés omitirlo y hacerlo más tarde desde Ajustes.",
              )}
            </p>

            <div className="fx-onb-cfe-import-actions">
              <p className="title">
                {t("settings.cfe.importGroupLabel")}
              </p>
              <p className="sub">
                {t("settings.cfe.importHint")}
              </p>
              <div className="actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setImportEmittedOpen(true)}
                >
                  <Upload className="mr-2 h-4 w-4 shrink-0" />
                  {t("settings.cfe.importEmitted")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setImportRecibidasOpen(true)}
                >
                  <Upload className="mr-2 h-4 w-4 shrink-0" />
                  {t("settings.cfe.importRecibidas")}
                </Button>
              </div>
            </div>

            {(onBack || onNext) && (
              <div className="fx-onb-actions" style={{ marginTop: 28 }}>
                <div className="side">
                  {onBack ? (
                    <button
                      type="button"
                      className="fx-onb-btn fx-onb-btn-secondary"
                      onClick={onBack}
                    >
                      <ArrowLeft aria-hidden />
                      {t("onboarding.back", "Volver")}
                    </button>
                  ) : null}
                </div>
                <div className="side end" style={{ gap: 8 }}>
                  {onSkip ? (
                    <button
                      type="button"
                      className="fx-onb-btn fx-onb-btn-ghost"
                      onClick={onSkip}
                      disabled={nextLoading}
                    >
                      {t("onboarding.stepCfeImportSkipButton")}
                    </button>
                  ) : null}
                  {onNext ? (
                    <button
                      type="button"
                      className="fx-onb-btn fx-onb-btn-accent"
                      onClick={() => void onNext()}
                      disabled={nextDisabled || nextLoading}
                    >
                      {nextLoading ? (
                        <Loader2 className="animate-spin" aria-hidden />
                      ) : null}
                      {t("onboarding.next", "Siguiente")}
                      <ArrowRight aria-hidden />
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </>
        }
        brand={<CfeImportBrandColumn />}
      />

      <ImportEmittedCfeDialog
        tenantId={tenantId}
        tenantName={tenantDisplayName}
        open={importEmittedOpen}
        onOpenChange={setImportEmittedOpen}
        importFn={importEmittedCfes}
        onSuccess={onImportSuccess}
      />
      <ImportRecibidasCfeDialog
        tenantId={tenantId}
        tenantName={tenantDisplayName}
        open={importRecibidasOpen}
        onOpenChange={setImportRecibidasOpen}
        importFn={importRecibidasCfes}
        onSuccess={onImportSuccess}
      />
    </>
  );
}
